/**
 * 빌드 화면 위 진입 카드 껍데기 — AI 생성(주제·자료)과 문제 은행 카드가 같은 모양을 쓴다.
 * 카드 전체가 하나의 버튼이다. ZUS 에 카드 컴포넌트가 없어 버튼 위에 카드 모양을 얹는다.
 * - 크기: 빈 퀴즈는 크게(160), 문제가 있으면 작게(116). 모바일은 늘 크게.
 * - 높게(tall): 크기와 별개로 카드 높이만 tablet-sm 이상에서 200 — 아래 다른 카드가 없어 두 장만 남을 때.
 * - hover: 그림자(shadow-sm) + 안의 그림 4px 위로(BUILD_ENTRY_PICTURE_HOVER_CLASS), 150ms ease-out.
 * 그림은 카드 모서리에서 잘린다(overflow-hidden).
 */
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { ChevronRightIcon } from '@zep-us/zus-icons';
import { cn } from '@/utils/tailwind.util';
export type BuildEntryCardSize = 'small' | 'large';

/** 카드 hover 에 그림만 4px 위로 — 150ms ease-out. 카드 안 그림이 붙인다. */
export const BUILD_ENTRY_PICTURE_HOVER_CLASS = 'transition-transform duration-150 ease-out group-hover:-translate-y-[4px] motion-reduce:transition-none';
const CARD_HEIGHT: Record<BuildEntryCardSize, string> = {
  // 모바일은 늘 크게(PRD 3-1 — 모바일 전용 크기 없음, 빈 퀴즈·모바일 = 크게).
  small: 'h-[160px] tablet-sm:h-[116px]',
  large: 'h-[160px]'
};

/** 높게 — 모바일은 세로 1열이라 두 장이 첫 화면을 다 차지하지 않게 160 그대로 둔다. */
const TALL_CARD_HEIGHT_CLASS = 'tablet-sm:h-[200px]';

/** Figma OptionCard size=lg 는 body/lg/bold, size=sm 은 body/md/bold. */
const CARD_TITLE_CLASS: Record<BuildEntryCardSize, string> = {
  small: 'text-body-lg-bold tablet-sm:text-body-md-bold',
  large: 'text-body-lg-bold'
};
export const BuildEntryCard = ({
  title,
  size,
  tall = false,
  onClick,
  testId,
  className,
  children
}: {
  title: string;
  size: BuildEntryCardSize;
  /** 카드 높이만 키운다(tablet-sm 이상 200). 제목·그림 크기는 size 그대로다. */
  tall?: boolean;
  onClick: () => void;
  testId: string;
  className?: string;
  children: React.ReactNode;
}) => <button type="button" data-testid={testId} data-tall={tall} onClick={onClick} className={cn('group relative flex w-full flex-col items-start overflow-hidden rounded-md border border-alternative bg-default text-left', 'transition-shadow duration-150 ease-out hover:shadow-sm focus-visible:focus-ring motion-reduce:transition-none', CARD_HEIGHT[size], tall && TALL_CARD_HEIGHT_CLASS, className)} data-sentry-component="BuildEntryCard" data-sentry-source-file="BuildEntryCard.tsx">
    <span className={cn('flex items-center gap-4xs px-md pt-md text-default', CARD_TITLE_CLASS[size])}>
      {title}
      <ChevronRightIcon size="2xs" fill={ICON_COLOR.default} aria-hidden data-sentry-element="ChevronRightIcon" data-sentry-source-file="BuildEntryCard.tsx" />
    </span>
    {/* 그림 칸 — 머리 아래 남은 자리 전체(카드 폭). 그림은 이 칸 기준으로 놓인다. */}
    <span className="relative w-full flex-1">{children}</span>
  </button>;